import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { EssayService } from '../../services/essayService';
import { Essay } from '../../types/Essay';
import { makeAuthenticatedRequest } from '../../services/authService';
import PageHeader from '../common/PageHeader';
import './EssayEditor.css';

interface EssayEditorProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const EssayEditor: React.FC<EssayEditorProps> = ({ user, onToggleSidebar }) => {
  const { essayId } = useParams<{ essayId: string }>();
  const navigate = useNavigate();
  
  const [essay, setEssay] = useState<Essay | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Track current content being edited
  const [currentTitle, setCurrentTitle] = useState<string>('');
  const [currentContent, setCurrentContent] = useState<string>('');
  const [currentStatus, setCurrentStatus] = useState<'pending' | 'on-going' | 'completed'>('pending');
  
  // Sync status state
  const [syncStatus, setSyncStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  
  // AI generation state
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Menu state
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Save timeout ref
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (essayId && user) {
      loadEssay();
    }
  }, [essayId, user]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Update sync status display every 10 seconds
  useEffect(() => {
    if (lastSyncTime && syncStatus === 'saved') {
      const interval = setInterval(() => {
        setLastSyncTime(new Date(lastSyncTime));
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [lastSyncTime, syncStatus]);

  const loadEssay = async () => {
    if (!essayId || !user) return;

    try {
      setLoading(true);
      setError(null);
      
      const data = await EssayService.getEssayById(essayId, user.id);
      setEssay(data);
      setCurrentTitle(data.title);
      setCurrentContent(data.content || '');
      setCurrentStatus(data.status);
    } catch (err) {
      console.error('Error loading essay:', err);
      setError('Failed to load essay');
    } finally {
      setLoading(false);
    }
  };

  // Auto-save debounced
  const debouncedSave = useCallback(async (title: string, content: string, status?: 'pending' | 'on-going' | 'completed') => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(async () => {
      if (!essayId) return;
      
      try {
        setSyncStatus('saving');
        await EssayService.updateEssay(essayId, {
          title,
          content,
          status: status || currentStatus
        });
        setSyncStatus('saved');
        setLastSyncTime(new Date());
      } catch (err) {
        console.error('Error updating essay:', err);
        setSyncStatus('error');
        setError('Failed to save essay');
      }
    }, 500);
  }, [essayId, currentStatus]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setCurrentTitle(newTitle);
    if (essay) {
      debouncedSave(newTitle, currentContent);
    }
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setCurrentContent(newContent);
    if (essay) {
      debouncedSave(currentTitle, newContent);
    }
  };

  const handleStatusChange = async (newStatus: 'pending' | 'on-going' | 'completed') => {
    if (!essay) return;
    
    setCurrentStatus(newStatus);
    try {
      setSyncStatus('saving');
      await EssayService.updateEssay(essay.essay_id, {
        title: currentTitle,
        content: currentContent,
        status: newStatus
      });
      setSyncStatus('saved');
      setLastSyncTime(new Date());
      setEssay({ ...essay, status: newStatus });
    } catch (err) {
      console.error('Error updating essay status:', err);
      setSyncStatus('error');
      setError('Failed to update essay status');
      // Revert status on error
      setCurrentStatus(essay.status);
    }
    setIsMenuOpen(false);
  };

  const handleBack = () => {
    navigate('/essays');
  };

  const handleDelete = async () => {
    if (!essayId) return;
    
    if (window.confirm('Are you sure you want to delete this essay? This action cannot be undone.')) {
      try {
        await EssayService.deleteEssay(essayId);
        navigate('/essays');
      } catch (err) {
        console.error('Error deleting essay:', err);
        setError('Failed to delete essay');
      }
    }
    setIsMenuOpen(false);
  };

  const handleAIGenerate = async () => {
    if (!user || !essayId) return;

    try {
      setIsGenerating(true);
      setError(null);

      const prompt = `Continue writing the next few sentences for this essay. Maintain the same writing style and tone. Here is the current essay content:\n\nTitle: ${currentTitle}\n\nContent:\n${currentContent}`;

      const response = await makeAuthenticatedRequest('/ai/chat', {
        method: 'POST',
        body: JSON.stringify({
          provider: 'claude',
          model: 'claude-3-haiku-20240307',
          systemPrompt: 'You are a helpful writing assistant. Continue the essay naturally, maintaining the same style and tone. Only generate a few sentences.',
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          maxTokens: 500,
          temperature: 0.7
        })
      });

      if (response.success && response.data) {
        // Try to get content from response.data.response or response.data.content
        let generatedText = '';
        if (response.data.response && typeof response.data.response === 'string') {
          generatedText = response.data.response;
        } else if (response.data.content) {
          // If content is an array (Claude format), extract text from first block
          if (Array.isArray(response.data.content) && response.data.content.length > 0) {
            generatedText = response.data.content[0].text || '';
          } else if (typeof response.data.content === 'string') {
            generatedText = response.data.content;
          }
        }
        
        if (generatedText) {
          const trimmedText = generatedText.trim();
          const newContent = currentContent + (currentContent ? ' ' : '') + trimmedText;
          setCurrentContent(newContent);
          
          // Auto-save the updated content
          if (essay) {
            debouncedSave(currentTitle, newContent);
          }
        } else {
          throw new Error('No content generated');
        }
      } else {
        throw new Error('Failed to generate content');
      }
    } catch (err) {
      console.error('Error generating AI content:', err);
      setError('Failed to generate content. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const getSyncStatusText = () => {
    if (syncStatus === 'saving') {
      return 'Saving...';
    } else if (syncStatus === 'error') {
      return 'Save failed';
    } else if (lastSyncTime) {
      const now = new Date();
      const diffMs = now.getTime() - lastSyncTime.getTime();
      const diffSecs = Math.floor(diffMs / 1000);
      
      if (diffSecs < 5) {
        return 'Saved just now';
      } else if (diffSecs < 60) {
        return `Saved ${diffSecs}s ago`;
      } else if (diffSecs < 3600) {
        const mins = Math.floor(diffSecs / 60);
        return `Saved ${mins}m ago`;
      } else {
        const hours = Math.floor(diffSecs / 3600);
        return `Saved ${hours}h ago`;
      }
    }
    return 'Saved';
  };

  if (loading) {
    return (
      <div className="essay-editor-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Loading...</h1>}
          rightActions={
            <button 
              className="essay-editor-close-btn"
              onClick={handleBack}
            >
              ✕
            </button>
          }
        />
        <div className="essay-editor-loading">Loading essay...</div>
      </div>
    );
  }

  if (error && !essay) {
    return (
      <div className="essay-editor-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Error</h1>}
          rightActions={
            <button 
              className="essay-editor-close-btn"
              onClick={handleBack}
            >
              ✕
            </button>
          }
        />
        <div className="essay-editor-error">
          {error || 'Essay not found'}
        </div>
      </div>
    );
  }

  if (!essay) {
    return null;
  }

  return (
    <div className="essay-editor-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <div className="essay-editor-header-center">
            <span className={`essay-sync-status ${syncStatus}`}>
              {syncStatus === 'saving' && '⟳'}
              {syncStatus === 'saved' && '✓'}
              {syncStatus === 'error' && '✕'}
              {' '}
              {getSyncStatusText()}
            </span>
          </div>
        }
        leftActions={
          <button 
            className="essay-back-btn"
            onClick={handleBack}
          >
            ← Back
          </button>
        }
        rightActions={
          <div className="essay-editor-right-actions">
            <div className="essay-editor-menu" ref={menuRef}>
              <button 
                className="essay-menu-btn"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                ⋮
              </button>
              {isMenuOpen && (
                <div className="essay-menu-dropdown">
                  <div className="essay-menu-section">
                    <div className="essay-menu-label">Status</div>
                    <select
                      className="essay-menu-status-select"
                      value={currentStatus}
                      onChange={(e) => handleStatusChange(e.target.value as 'pending' | 'on-going' | 'completed')}
                    >
                      <option value="pending">Pending</option>
                      <option value="on-going">On-going</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                  <div className="essay-menu-separator"></div>
                  <button 
                    className="essay-menu-item"
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleAIGenerate();
                    }}
                    disabled={isGenerating}
                  >
                    {isGenerating ? '⟳ Generating...' : '✨ AI Generate'}
                  </button>
                  <button 
                    className="essay-menu-item"
                    onClick={handleDelete}
                  >
                    Delete Essay
                  </button>
                </div>
              )}
            </div>
          </div>
        }
      />

      {error && (
        <div className="essay-editor-error-banner">
          {error}
          <button onClick={() => setError(null)} className="essay-error-close">✕</button>
        </div>
      )}

      <div className="essay-editor-content">
        <div className="essay-title-input-container">
          <input
            type="text"
            className="essay-title-input"
            placeholder="Essay title..."
            value={currentTitle}
            onChange={handleTitleChange}
          />
        </div>
        <div className="essay-content-container">
          <textarea
            className="essay-content-textarea"
            placeholder="Start writing your essay..."
            value={currentContent}
            onChange={handleContentChange}
          />
        </div>
      </div>
    </div>
  );
};

export default EssayEditor;

